import { v4 as uuid } from 'uuid';

import {
  setSubscribeUsersStatusFailed,
  setSubscribeUsersStatusPending,
  setSubscribeUsersStatusSuccess,
} from './actions';
import { GuardError } from '../../errors/GuardError';
import { setSocketStatusFailed } from '../../slices/socketStatusSlice';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { IWSClientSubscribeUsersStatusSchema } from '../../types/usersStatus';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { ERequestSource } from '../../utils/logging/publicApiDiagnostics';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { ensureEventChannelOpen } from '../socket/ensureEventChannelOpen';

/**
 * Подписка по сокету на получение статуса по массиву id пользователей
 */
export const subscribeUsersStatus =
  (requestedUserIds: number[]): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { clientEvents, eventChannel } = socket;

    const {
      user: { userId: currentUserId },
    } = getState();

    dispatch(setSubscribeUsersStatusPending());

    const uid = uuid();

    const isConnected = await dispatch(ensureEventChannelOpen(ERequestSource.WsReconnectSubscribeStatus));

    if (!isConnected) {
      clientEvents.set(uid, { type: EWsClientEventType.SubscribeUsersStatus, status: ERequestApiStatus.Failed });
      dispatch(setSubscribeUsersStatusFailed());
      dispatch(setSocketStatusFailed());

      telemetry.increment(ETelemetryEvents.ChatWsUserStatusFail);

      const exception = new GuardError({
        domain: EExceptionDomain.Ws,
        message: 'Не удалось подписаться на статус пользователей по ws: соединение закрыто',
      });

      logger.warning(exception);

      return;
    }

    const wsStatusEvent: IWSClientSubscribeUsersStatusSchema = {
      type: 'status',
      uid,
      userId: currentUserId,
      requestedUserIds,
      action: 'subscribe',
    };

    clientEvents.set(uid, {
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.SubscribeUsersStatus,
    });

    try {
      await timeoutPromise(eventChannel.subscribeUsersStatus(wsStatusEvent), 10000, EExceptionDomain.Ws);
      dispatch(setSubscribeUsersStatusSuccess());
    } catch (err) {
      const exception = getGenericLikeError({
        error: err,
        domain: EExceptionDomain.Ws,
        message: 'Ошибка при подписке на статус пользователей по ws',
      });

      logger.warning(exception);
      telemetry.increment(ETelemetryEvents.ChatWsUserStatusFail);

      clientEvents.set(uid, { type: EWsClientEventType.SubscribeUsersStatus, status: ERequestApiStatus.Failed });
      dispatch(setSubscribeUsersStatusFailed());
    }
  };
